/* eslint-disable import/no-cycle */
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { pickBy } from 'lodash';

import {
  ALBUM_DUBBING_FIELDS_QUERY,
  ALBUM_QUERY,
  ALBUM_TRANSLATION_FIELDS_QUERY,
  SHOWCASE_QUERY,
  SHOWCASE_TRANSLATION_FIELDS_QUERY,
} from '../gql/album-gql';
import { CUSTOMER_TYPE_TEMPLATE_FLAGS_QUERY } from '../gql/misc-gql';
import { STORY_REFERENCING_ALBUMS_ACCESS_QUERY } from '../gql/story-gql';
import { populateShowcaseStoriesWithOriginalVideo } from '../utility/story';

import type ApiClient from '../apiClient/ApiClient';
import type { AiVoiceRecord } from '../types.ts/aiVoiceRecord';
import type { Language } from '../types.ts/language';
import type {
  DatoShowcase,
  FolderStructure,
  Showcase,
  ShowcaseAccess,
  ShowcaseBranding,
} from '../types.ts/story';
import type { TranslationHandlerRecord } from '../types.ts/translationHandlerRecord';
import type { Client } from '@datocms/cma-client-browser';
import type { GraphQLClient, RequestDocument } from 'graphql-request';

type TranslationFields = {
  translationsEnabled: boolean;
  supportedLanguages: Language[];
  translationHandlerOverride: TranslationHandlerRecord | null;
};

const EMPTY_TRANSLATION_FIELDS: TranslationFields = {
  translationsEnabled: false,
  supportedLanguages: [],
  translationHandlerOverride: null,
};

type DubbingFields = {
  dubbingEnabled: boolean;
  activatedAiVoices: AiVoiceRecord[];
};

const EMPTY_DUBBING_FIELDS: DubbingFields = {
  dubbingEnabled: false,
  activatedAiVoices: [],
};

type CustomerTypeTemplateFlags = {
  excludeFromGlobalTemplate: boolean;
  excludeFromGlobalCaptions: boolean;
};

type FindShowcaseOptions = {
  // Fetches `originalVideo` for every story in the workspace — one slow
  // populate request per 50 stories. Opt in only when a caller reads
  // `stories[].originalVideo`.
  populateStoryVideos?: boolean;
};

const EMPTY_CUSTOMER_TYPE_TEMPLATE_FLAGS: CustomerTypeTemplateFlags = {
  excludeFromGlobalTemplate: false,
  excludeFromGlobalCaptions: false,
};

/**
 * Shared write serializer for the JSON branding font fields (`brand_fonts`,
 * `custom_fonts`, `font_families`, `font_styles`). These are DatoCMS JSON
 * fields: GraphQL **reads** return already-parsed arrays (which is why
 * `branding.customFonts` is consumed directly), and CMA **writes** take a
 * serialized value — so on the write path we `JSON.stringify` the in-memory
 * array here.
 *
 * The string-passthrough branch is a **double-encode guard**: if a value is
 * ever already a string (a legacy/not-yet-migrated record, or a store
 * intermediate state), re-stringifying it would double-encode and DatoCMS
 * rejects it ("Failed to fetch" at the client layer). Do NOT remove that
 * branch — it prevents data corruption, it is not dead code.
 *
 * Tolerates either shape:
 *   - null / undefined → `null` (or `'[]'` for fields that prefer
 *     empty arrays over null)
 *   - already a string → passed through verbatim (guard above)
 *   - any other value (array / object) → `JSON.stringify`d
 */
const serializeBrandingJsonField = (
  value: unknown,
  emptyFallback: 'null' | 'empty-array' = 'null',
): string | null => {
  if (value === null || value === undefined) {
    return emptyFallback === 'empty-array' ? '[]' : null;
  }
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
};

export class AlbumRepository {
  private dClient: Client | ApiClient;

  private gqlClient: GraphQLClient;
  // private dClient: Client;

  private logger = LogManager.getLogger('repository:AlbumRepository');

  constructor(dClient: Client | ApiClient, gqlClient: GraphQLClient) {
    this.gqlClient = gqlClient;
    this.dClient = dClient;
  }

  async findAllWithStoryAccess(storyId: string): Promise<ShowcaseAccess[]> {
    const response = (await this.gqlClient.request(
      STORY_REFERENCING_ALBUMS_ACCESS_QUERY,
      {
        id: storyId,
      },
    )) as { story: { albums: ShowcaseAccess[] } } | null;

    return response?.story.albums || [];
  }

  async findOneBySlug(
    slug: string,
    options: FindShowcaseOptions = {},
  ): Promise<Showcase | null> {
    const response = await this.gqlClient.request<{ showcase?: DatoShowcase }>(
      SHOWCASE_QUERY,
      {
        slug,
      },
    );
    if (!response.showcase) return null;

    const customerTypeId = response.showcase.customerType?.id;
    const [translationFields, customerTypeTemplateFlags] = await Promise.all([
      this.fetchTranslationFieldsBySlug(slug),
      customerTypeId
        ? this.fetchCustomerTypeTemplateFlagsById(customerTypeId)
        : Promise.resolve(EMPTY_CUSTOMER_TYPE_TEMPLATE_FLAGS),
    ]);

    return this.mergeShowcaseFields(
      response.showcase,
      translationFields,
      customerTypeTemplateFlags,
      options,
    );
  }

  async findOne(
    id: string,
    options: FindShowcaseOptions = {},
  ): Promise<Showcase | null> {
    const response = await this.gqlClient.request<{ showcase?: DatoShowcase }>(
      ALBUM_QUERY,
      {
        id,
      },
    );
    if (!response.showcase) return null;

    const customerTypeId = response.showcase.customerType?.id;
    const [translationFields, customerTypeTemplateFlags] = await Promise.all([
      this.fetchTranslationFieldsById(id),
      customerTypeId
        ? this.fetchCustomerTypeTemplateFlagsById(customerTypeId)
        : Promise.resolve(EMPTY_CUSTOMER_TYPE_TEMPLATE_FLAGS),
    ]);

    return this.mergeShowcaseFields(
      response.showcase,
      translationFields,
      customerTypeTemplateFlags,
      options,
    );
  }

  private async mergeShowcaseFields(
    showcase: DatoShowcase,
    translationFields: TranslationFields,
    customerTypeTemplateFlags: CustomerTypeTemplateFlags,
    { populateStoryVideos = false }: FindShowcaseOptions,
  ): Promise<Showcase> {
    const merged: DatoShowcase = {
      ...showcase,
      ...translationFields,
      ...(showcase.customerType && {
        customerType: {
          ...showcase.customerType,
          ...customerTypeTemplateFlags,
        },
      }),
    };

    return populateStoryVideos
      ? populateShowcaseStoriesWithOriginalVideo(merged)
      : merged;
  }

  // Translation-related showcase fields live in a separate query so schema
  // drift (migration not yet applied in the target environment) never blocks
  // the primary showcase load. On any failure we log and default to empty.
  private async fetchTranslationFields(
    query: RequestDocument,
    vars: Record<string, unknown>,
    context: Record<string, unknown>,
  ): Promise<TranslationFields> {
    try {
      const response = (await this.gqlClient.request(query, vars)) as {
        showcase?: {
          translationsEnabled?: boolean | null;
          supportedLanguages?: Language[] | null;
          translationHandlerOverride?: TranslationHandlerRecord | null;
        };
      };
      return {
        translationsEnabled: response.showcase?.translationsEnabled ?? false,
        supportedLanguages: response.showcase?.supportedLanguages ?? [],
        translationHandlerOverride:
          response.showcase?.translationHandlerOverride ?? null,
      };
    } catch (err) {
      this.logger.warn(
        'Showcase translation fields fetch failed; defaulting to empty',
        { ...context, err },
      );
      return EMPTY_TRANSLATION_FIELDS;
    }
  }

  private fetchTranslationFieldsBySlug(slug: string) {
    return this.fetchTranslationFields(
      SHOWCASE_TRANSLATION_FIELDS_QUERY,
      { slug },
      { slug },
    );
  }

  // Public so callers that source a showcase from other entry points (e.g.
  // VideoCreatorStore.initializeWithStory, which pulls the organization from
  // story._allReferencingShowcases) can hydrate the translation fields too.
  async fetchTranslationFieldsById(id: string) {
    return this.fetchTranslationFields(
      ALBUM_TRANSLATION_FIELDS_QUERY,
      { id },
      { id },
    );
  }

  // Dubbing-related showcase fields live in a separate query for the same
  // schema-drift resilience reason as the translation fields above (DE3). On
  // any failure we log and default to empty (dubbing off, no voices).
  private async fetchDubbingFields(
    query: RequestDocument,
    vars: Record<string, unknown>,
    context: Record<string, unknown>,
  ): Promise<DubbingFields> {
    try {
      const response = (await this.gqlClient.request(query, vars)) as {
        showcase?: {
          dubbingEnabled?: boolean | null;
          activatedAiVoices?: AiVoiceRecord[] | null;
        };
      };
      return {
        dubbingEnabled: response.showcase?.dubbingEnabled ?? false,
        activatedAiVoices: response.showcase?.activatedAiVoices ?? [],
      };
    } catch (err) {
      this.logger.warn(
        'Showcase dubbing fields fetch failed; defaulting to empty',
        { ...context, err },
      );
      return EMPTY_DUBBING_FIELDS;
    }
  }

  // Public so VideoCreatorStore.initializeWith* can eagerly hydrate the dubbing
  // activation fields (mirrors fetchTranslationFieldsById). A by-slug counterpart
  // (SHOWCASE_DUBBING_FIELDS_QUERY) exists for when a findOneBySlug-style merge
  // is wired in a later surface.
  async fetchDubbingFieldsById(id: string) {
    return this.fetchDubbingFields(ALBUM_DUBBING_FIELDS_QUERY, { id }, { id });
  }

  // Resilient fetch for the excludeFromGlobalTemplate flag on a CustomerType.
  // The field lives in its own query so the primary showcase query (which
  // embeds CUSTOMER_TYPE_QUERY_FRAGMENT) is not blocked by schema drift.
  // Public so callers that source a showcase from STORY_QUERY's
  // _allReferencingShowcases can hydrate the flag too.
  async fetchCustomerTypeTemplateFlagsById(
    customerTypeId: string,
  ): Promise<CustomerTypeTemplateFlags> {
    try {
      const response = await this.gqlClient.request<{
        customerType?: CustomerTypeTemplateFlags;
      }>(CUSTOMER_TYPE_TEMPLATE_FLAGS_QUERY, { id: customerTypeId });
      return {
        excludeFromGlobalTemplate:
          response.customerType?.excludeFromGlobalTemplate ?? false,
        excludeFromGlobalCaptions:
          response.customerType?.excludeFromGlobalCaptions ?? false,
      };
    } catch (err) {
      this.logger.warn(
        'CustomerType template flag fetch failed; defaulting to empty',
        { customerTypeId, err: extractErrorInfo(err) },
      );
      return EMPTY_CUSTOMER_TYPE_TEMPLATE_FLAGS;
    }
  }

  async update(showcase: Partial<Showcase> & { id: string }): Promise<void> {
    if (!showcase.id) {
      throw new Error('Organization id is required');
    }

    let brandingId = showcase.branding?.id;
    if (showcase.branding) {
      if (showcase.branding.id) await this.updateBranding(showcase.branding);
      else {
        const branding = await this.createBranding(showcase.branding);
        brandingId = branding.id;
      }
    }

    await this.dClient.items.update(showcase.id, {
      ...(showcase.title && {
        title: { en: showcase.title },
      }),

      ...(showcase.organizationArtifacts && {
        organization_artifacts: showcase.organizationArtifacts.map(photo => ({
          upload_id: photo.id,
          ...(photo.title && { title: photo.title }),
        })),
      }),

      ...(showcase.organizationLogos && {
        organization_logos: showcase.organizationLogos.map(photo => ({
          upload_id: photo.id,
          ...(photo.title && { title: photo.title }),
        })),
      }),

      ...(showcase.organizationAudios && {
        organization_audios: showcase.organizationAudios.map(audio => audio.id),
      }),

      ...(showcase.organizationArtifactsVideo && {
        organization_artifacts_video: showcase.organizationArtifactsVideo.map(
          video => ({
            upload_id: video.id,
            ...(video.title && { title: video.title }),
          }),
        ),
      }),

      ...(showcase.contributors && {
        contributors: showcase.contributors.map(c => c.id),
      }),

      ...(showcase.lowerThirdTextBranding && {
        lower_third_text_branding: showcase.lowerThirdTextBranding.map(
          b => b.id,
        ),
      }),

      ...(showcase.karaokeTextBranding && {
        karaoke_text_branding: showcase.karaokeTextBranding.map(b => b.id),
      }),

      ...(showcase.compositionsBranding && {
        compositions_branding: showcase.compositionsBranding.map(b => b.id),
      }),

      ...(showcase.quoteCardsBranding && {
        quote_cards_branding: showcase.quoteCardsBranding.map(b => b.id),
      }),

      ...(showcase.lastUsedTemplates && {
        last_used_templates: JSON.stringify(
          showcase.lastUsedTemplates,
          null,
          2,
        ),
      }),

      ...(showcase.branding && { branding: brandingId }),

      ...(showcase.defaultVideoTemplate !== undefined && {
        default_video_template: showcase.defaultVideoTemplate?.id ?? null,
      }),

      ...(showcase.defaultPullQuoteTemplate !== undefined && {
        default_pull_quote_template:
          showcase.defaultPullQuoteTemplate?.id ?? null,
      }),

      ...(showcase.translationsEnabled !== undefined && {
        translations_enabled: showcase.translationsEnabled,
      }),

      ...(showcase.defaultLanguage !== undefined && {
        default_language: showcase.defaultLanguage ?? null,
      }),

      ...(showcase.supportedLanguages !== undefined && {
        supported_languages: (showcase.supportedLanguages ?? []).map(l => l.id),
      }),

      ...(showcase.dubbingEnabled !== undefined && {
        dubbing_enabled: showcase.dubbingEnabled,
      }),

      ...(showcase.activatedAiVoices !== undefined && {
        activated_ai_voices: (showcase.activatedAiVoices ?? []).map(v => v.id),
      }),
    });

    await this.dClient.items.publish(showcase.id);
  }

  // TODO(PLAT-9712): wrap with optimistic-locking retry like the stories list
  // update path (PLAT-9054). Folder writes hit the same showcase record and
  // race with story-list updates.
  async updateFolderStructure(
    showcaseId: string,
    folderStructure: FolderStructure,
  ): Promise<void> {
    await this.dClient.items.update(showcaseId, {
      folder_structure: folderStructure,
    });
    await this.dClient.items.publish(showcaseId);
  }

  async createBranding(branding: Partial<ShowcaseBranding>) {
    const itemType = await this.dClient.itemTypes.find('branding');
    const createdItem = await this.dClient.items.create({
      item_type: { type: 'item_type', id: itemType.id },
      title: branding.title || '',
      primary_colors: branding.primaryColors || '',
      secondary_colors: branding.secondaryColors || '',
      brand_fonts: serializeBrandingJsonField(
        branding.brandFonts,
        'empty-array',
      ),
      custom_fonts: serializeBrandingJsonField(branding.customFonts),
      font_families: serializeBrandingJsonField(branding.fontFamilies),
      font_styles: serializeBrandingJsonField(branding.fontStyles),
      custom_font_files:
        branding.customFontFiles?.map(font => ({
          upload_id: font.id,
          ...(font.title && { title: font.title }),
        })) || null,
      design_elements:
        branding.designElements?.map(designElement => ({
          upload_id: designElement.id,
          ...(designElement.title && { title: designElement.title }),
        })) || [],
      brand_voice: branding.brandVoice || '',
      avoid_words: branding.avoidWords || '',
      organization_website: branding.organizationWebsite || '',
      external_links: branding.externalLinks || '',
      custom_vocabulary: branding.customVocabulary || '',
      goals: branding.goals?.map(goal => goal.id) || [],
      audiences: branding.audiences?.map(audience => audience.id) || [],
    });
    return createdItem;
  }

  async updateBranding(branding: Partial<ShowcaseBranding>) {
    if (!branding.id) {
      throw new Error('Branding id is required');
    }

    await this.dClient.items.update(
      branding.id,
      pickBy(
        {
          title: branding.title,
          primary_colors: branding.primaryColors,
          secondary_colors: branding.secondaryColors,
          brand_fonts: serializeBrandingJsonField(
            branding.brandFonts,
            'empty-array',
          ),
          custom_fonts: serializeBrandingJsonField(branding.customFonts),
          font_families: serializeBrandingJsonField(branding.fontFamilies),
          font_styles: serializeBrandingJsonField(branding.fontStyles),
          custom_font_files:
            branding.customFontFiles?.map(font => ({
              upload_id: font.id,
              ...(font.title && { title: font.title }),
            })) || null,
          design_elements: branding.designElements?.map(designElement => ({
            upload_id: designElement.id,
            ...(designElement.title && { title: designElement.title }),
          })),
          brand_voice: branding.brandVoice,
          avoid_words: branding.avoidWords,
          organization_website: branding.organizationWebsite,
          external_links: branding.externalLinks,
          custom_vocabulary: branding.customVocabulary || '',
          goals: branding.goals?.map(goal => goal.id),
          audiences: branding.audiences?.map(audience => audience.id),
        },
        value => value !== undefined,
      ),
    );

    await this.dClient.items.publish(branding.id);
  }
}
